import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  icon?: React.ComponentType<{ className?: string }>
  subheading?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  bodyClassName?: string
  /** When true, header (icon, title, close) is hidden – for centered confirmation layouts */
  showHeader?: boolean
  /** When false, clicking overlay/backdrop does not close modal. */
  closeOnOverlayClick?: boolean
}

export default function Modal({
  isOpen,
  onClose,
  title,
  icon: Icon,
  subheading,
  children,
  footer,
  className = '',
  bodyClassName,
  showHeader = true,
  closeOnOverlayClick = true,
}: ModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 "
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      <div className="absolute inset-0 bg-black opacity-60 transition-opacity" />

      <div
        className={`relative flex w-full max-w-2xl max-h-[90vh] flex-col rounded-xl bg-white shadow-xl dark:bg-background-white dark:border dark:border-border ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {showHeader && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border-light dark:border-border shrink-0">
              <div className="flex items-center gap-4 flex-1">
                {Icon && (
                  <div className="w-12 h-12 rounded-lg bg-primary-light flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-text-primary">{title}</h2>
                  {subheading && (
                    <p className="text-sm text-text-secondary mt-1">{subheading}</p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 text-text-secondary hover:text-text-primary hover:bg-background rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* Body - Scrollable */}
        <div className={bodyClassName ?? 'flex-1 overflow-y-auto p-6'}>{children}</div>

        {/* Footer - Always Visible */}
        {footer && (
          <div className=" border-border-light p-2 shrink-0 flex justify-center items-center dark:border-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

